import { LaneLine, TargetSource, TrackedTarget } from "@app/shared";
import { getConfiguredLaneName } from "@app/shared/src/models/Shared.Lane.helpers";
import React from "react";

export type TargetDetailsPopupProps = {
    target: TrackedTarget | null;
    position: { x: number; y: number };
    onClose: () => void;
    laneLines?: LaneLine[];
};

export const TargetDetailsPopup: React.FC<TargetDetailsPopupProps> = ({ target, position, onClose, laneLines }) => {
    if (!target) return null;

    // Format values for display
    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return "N/A";
        if (typeof value === "number") return value.toFixed(3);
        if (typeof value === "boolean") return value ? "Yes" : "No";
        if (typeof value === "object") {
            if (value.x !== undefined && value.y !== undefined) {
                return `(${value.x?.toFixed(3)}, ${value.y?.toFixed(3)}, ${value.z?.toFixed(3) || 0})`;
            }
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    // Get target type for display
    const getTargetType = () => {
        if (target.associatedCameraTarget) return "Fused";
        if (target.source === TargetSource.Camera) return "Camera-Only";
        return "Radar-Only";
    };

    // Get all target properties for display
    const getTargetFields = () => {
        const decodedLane = getConfiguredLaneName(target.laneNumber, laneLines);
        const laneValue = decodedLane
            ? decodedLane.hasConfiguredName
                ? `${decodedLane.primary} (${decodedLane.rawLabel})`
                : decodedLane.primary
            : target.laneNumber;
        const fields: { label: string; value: any }[] = [
            { label: "Target Type", value: getTargetType() },
            { label: "Target ID", value: target.targetId },
            { label: "Radar ID", value: target.radarId },
            { label: "Source", value: target.source === TargetSource.Radar ? "Radar" : "Camera" },
            { label: "Lane", value: laneValue },
            { label: "Current Position", value: target.position },
            { label: "Velocity", value: target.velocity },
            { label: "First Frame Tracked", value: target.firstFrameTracked },
            { label: "Last Frame Tracked", value: target.lastFrameTracked },
            { label: "First Video Frame", value: target.firstVideoFrameTracked },
            { label: "Associated Camera Target", value: target.associatedCameraTarget ? "Yes" : "No" },
            { label: "List Index", value: target.listIndex },
            { label: "History Length", value: target.targetHistory?.length || 0 }
        ];

        return fields.filter(field => field.value !== null && field.value !== undefined);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                onClick={onClose}
            />

            {/* Popup */}
            <div
                className="fixed bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-y-auto"
                style={{
                    left: '30%',
                    top: '30%',
                    width: '40%',
                    height: '50%'
                }}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-800">
                        Target Details
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-3">
                    <div className="space-y-2">
                        {getTargetFields().map((field, index) => (
                            <div key={index} className="flex justify-between items-start text-sm">
                                <span className="font-medium text-gray-600 mr-2 min-w-0 flex-shrink-0">
                                    {field.label}:
                                </span>
                                <span className="text-gray-800 text-right break-words min-w-0 flex-1">
                                    {formatValue(field.value)}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Target History and Associated Camera Target Section */}
                    {(target.targetHistory && target.targetHistory.length > 0) && (
                        <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="flex gap-4">
                                {/* Target History Column */}
                                {target.targetHistory && target.targetHistory.length > 0 && (
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                            Target History
                                        </h4>
                                        <div className="space-y-1 text-xs">
                                            {target.targetHistory.map((history, index) => (
                                                <div key={index} className="bg-gray-50 p-2 rounded">
                                                    <div className="font-medium">Frame {history.frameNumber}</div>
                                                    <div>Pos: {formatValue(history.position)}</div>
                                                    <div>Vel: {formatValue(history.velocity)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Associated Camera Target Column */}
                                {target.associatedCameraTarget && (
                                    <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                            Associated Camera Target History
                                        </h4>
                                        <div className="space-y-1 text-xs">
                                            {target.associatedCameraTarget?.targetHistory.map((history, index) => (
                                                <div key={index} className="bg-gray-50 p-2 rounded">
                                                    <div className="font-medium">Frame {history.frameNumber}</div>
                                                    <div>Pos: {formatValue(history.position)}</div>
                                                    <div>Vel: {formatValue(history.velocity)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
